import { useCallback, useSyncExternalStore } from 'react';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
  duration?: number;
}

export interface Toast extends ToastOptions {
  id: string;
}

/**
 * One toast store for the whole app (BACKLOG Known Bugs #8, 2026-09-29).
 *
 * This hook used to keep its toasts in the calling component's own state,
 * and nothing rendered them, so every "Saved" on the site page (and in 37
 * other files) fired into the void. The store below is module-level:
 * `toast()` from anywhere lands in the one viewport mounted in main.tsx.
 * The call signature is unchanged, so no caller had to move.
 */
let toasts: Toast[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return toasts;
}

export function dismissToast(id: string) {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.delete(id);
  toasts = toasts.filter((x) => x.id !== id);
  emit();
}

export function showToast(options: ToastOptions): string {
  const id = Math.random().toString(36).slice(2, 11);
  const next: Toast = { id, duration: 5000, variant: 'default', ...options };
  toasts = [...toasts, next];
  emit();
  if (next.duration && next.duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismissToast(id), next.duration)
    );
  }
  return id;
}

/** Test helper: drop every toast and timer. */
export function clearToasts() {
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
  toasts = [];
  emit();
}

export const useToast = () => {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const toast = useCallback((options: ToastOptions) => showToast(options), []);
  const dismiss = useCallback((id: string) => dismissToast(id), []);
  return { toast, dismiss, toasts: current };
};
